import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { Alert, Box, Button, CircularProgress, Modal, Paper, TextField, Typography, useTheme, FormControl } from "@mui/material";
import { UserProfileError, AuthenticationError } from "../../contexts/UserContext";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { useGetUserDetail, useLogin } from "../../api/auth";
import { useAuthActions } from "../../store/auth";
import { AESCryptoService } from "../../utilities/cryptoService";
import { buildBasicAuthHeader, buildOAuthHeader } from "../../utilities/buildAuthHeader";
import { clearAppCookies } from "../../utilities/cookiesService";
import { clearAuthToken } from "../../utilities/authTokenService";
import { isValidRedirect } from "../../utilities/common";
import { useQueryClient } from "@tanstack/react-query";

interface LoginModalProps {
  open: boolean;
  onClose?: () => void;
}

const MIN_PASSWORD_LENGTH = 4;

export const LoginModal: React.FC<LoginModalProps> = ({ open, onClose = () => {} }) => {
  const theme = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({
    username: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    setUser,
    clearUser,
    setCommonAuthState: setCommonState,
    setBasicAuthHeader,
    setOAuthHeader,
    resetCommonAuthState
  } = useAuthActions();
  const [searchParams] = useSearchParams();
  const { error, isPending: isLoading, mutate } = useLogin();
  const { refetch: getUserDetailQuery, error: userProfileError } = useGetUserDetail({ enabled: false });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("🔐 LOGIN: Starting login process for user:", username);
    if (isLoading) return;
    if (!validate()) return;

    // clear all data of previous user
    clearAppCookies();
    clearAuthToken();
    localStorage.clear();
    queryClient.clear(); // Clear ALL React Query cache

    mutate(
      { password, username },
      {
        // Handle login fail
        onError(err) {
          console.error("❌ LOGIN: Login failed:", err);

          // CRITICAL: Ensure we don't revert to old user data on login failure
          // Keep everything cleared even if login fails
          console.log("🧹 LOGIN-ERROR: Ensuring all data remains cleared after login failure");
          resetCommonAuthState();
          localStorage.clear();
          clearAppCookies();

          // Clear auth tokens if available in `authTokenService
          clearAuthToken();

          // Override generic error message with server's response
          let errorMsg = "";
          if (isAxiosError(err)) {
            errorMsg = err.response?.data?.error_description;
          }
          err.message = new AuthenticationError(errorMsg || "Failed to login. Please try again.").message;
        },

        // Fetch user profile
        // Handle data encryption when storing
        async onSuccess(response: any) {
          const res = response.data;

          // Clear ALL authentication state immediately to prevent conflicts
          console.log("🧹 LOGIN: Clearing all session storage");
          clearUser();
          localStorage.clear();

          // Clear CloudFront cookies to prevent authentication conflicts
          // These cookies might belong to a previously logged-in user
          console.log("🍪 LOGIN: Clearing CloudFront cookies");

          const cryptoService = new AESCryptoService();

          // Store encrypted access & refresh token in session storage
          const encryptedAccessToken = cryptoService.encrypt(res.access_token);
          localStorage.setItem("accessToken", encryptedAccessToken?.toString());

          if (res.refresh_token) {
            const encryptedRefreshToken = cryptoService.encrypt(res.refresh_token);
            localStorage.setItem("refreshToken", encryptedRefreshToken?.toString());
          }

          // FIX: Clear module-level token cache so authTokenService reads fresh token from localStorage
          clearAuthToken();

          // Fetch user profile
          const userInfoResponse = await getUserDetailQuery();

          if (userInfoResponse.isError) {
            console.error("❌ LOGIN: User profile fetch failed:", userInfoResponse.error?.message);
            throw new UserProfileError("Failed to fetch user info");
          }

          const userInfo = userInfoResponse.data;

          if (!userInfo) {
            throw new UserProfileError("User details not found!");
          }

          setUser(userInfo);

          console.log("👤 LOGIN: User info received:", {
            email: userInfo?.email,
            name: `${userInfo?.first_name} ${userInfo?.last_name}`,
            is_staff: userInfo?.is_staff,
            is_superuser: userInfo?.is_superuser,
            organisation: userInfo?.organisation?.name
          });

          // initialize auth basic header only one time
          const basicAuthHeader = buildBasicAuthHeader();
          setBasicAuthHeader(basicAuthHeader);

          const oAuthHeader = buildOAuthHeader(res.access_token);
          setOAuthHeader(oAuthHeader);

          // Flag that indicates login & user profile has been successfully loaded
          setCommonState({ isInitialized: true, isAuthCheckComplete: true });

          // Navigate to redirect path or home page after successful login
          const redirectPathQuery = decodeURI(searchParams.get("redirect") || "");
          // If redirect path mal-form or invalid redirect to the 'index' page.
          // Otherwise navigate to redirect param path
          let from = isValidRedirect(redirectPathQuery) ? (redirectPathQuery as string) : "/";
          navigate(from, { replace: true });

          console.log("✅ LOGIN: Login completed successfully for:", userInfo?.email);
        }
      }
    );
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const customInputStyles = {
    "& .MuiFormHelperText-root": {
      marginLeft: 0
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#ccc"
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main
      }
    },

    // for input background color auto filling from cache
    "& input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px white inset",
      WebkitTextFillColor: "#000",
      caretColor: "#000"
    },
    "& input:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 1000px white inset"
    },
    "& input:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px white inset"
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!username?.trim()) {
      newErrors.username = "Username is required";
    }
    // This is commented because the username might not always the email
    // else if (!/\S+@\S+\.\S+/.test(username)) {
    //   newErrors.username = "Enter a valid email";
    // }

    if (!password?.trim()) {
      newErrors.password = "Password is required";
    } else if (password?.trim().length < MIN_PASSWORD_LENGTH) {
      newErrors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby='login-modal-title'>
      <Paper
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Box
          sx={{
            width: "28rem",
            border: "0.0625rem #D0D5DD solid",
            borderRadius: "0.5rem",
            paddingX: "1.5rem",
            paddingY: "2.625rem",
            display: "flex",
            flexDirection: "column",
            gap: "2.5rem",
            boxShadow: 2
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            <Typography variant='h5' sx={{ fontWeight: 700 }}>
              Sign In
            </Typography>
            <Typography variant='body1'>to continue to your account.</Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert
                severity='error'
                sx={{
                  mb: 2,
                  width: "100%",
                  borderRadius: 0.5
                }}
              >
                {error.message || userProfileError?.message}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: "24px", flexDirection: "column" }}>
              <Box sx={{ display: "flex", gap: "4px", flexDirection: "column" }}>
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  Username
                </Typography>
                <TextField
                  type='text'
                  aria-label='Username'
                  fullWidth
                  required
                  placeholder='Username'
                  onChange={(e) => setUsername(e.target.value)}
                  error={Boolean(errors.username)}
                  helperText={errors.username}
                  sx={customInputStyles}
                />
              </Box>
              <FormControl sx={{ display: "flex", gap: "4px", flexDirection: "column" }}>
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  required
                  placeholder='Password'
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  aria-label='Password'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={handleTogglePasswordVisibility}
                          edge='end'
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  error={Boolean(errors.password)}
                  helperText={errors.password}
                  sx={customInputStyles}
                />
              </FormControl>
              <Typography
                variant='body2'
                onClick={() => navigate("/forgot-password")}
                sx={{
                  alignSelf: "flex-end",
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  ":hover": { textDecoration: "underline" }
                }}
              >
                Forgot password?
              </Typography>
              <Button
                type='submit'
                variant='contained'
                disabled={!username || !password}
                fullWidth
                size='large'
                sx={{
                  mt: "24px",
                  backgroundColor: "grey.800",
                  height: "44px",
                  cursor: "pointer",
                  ":hover": { textDecoration: "underline", backgroundColor: "grey.700" }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={20} color='inherit' />
                ) : (
                  <Typography sx={{ color: "white", textTransform: "capitalize", px: 2 }}>Continue</Typography>
                )}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Modal>
  );
};
